import type { OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import type { Vector3 } from "three";
import * as THREE from "three";
import { LTCaseUserOption } from "../../enum/LTCaseUserOption";
import { PaddingStyle } from "../../enum/PaddingStyle";
import { PadSide } from "../../enum/PadSide";
import { LTPartType } from "../../enum/PartType";
import type { PaddingType } from "../../interfaces/Padding";
import type { PadDefinition } from "../../interfaces/PadProperties";
import { PadHelper } from "../../structure/PadHelper";
import { DefaultGroupName } from "../../structure/Rect";
import { RectPropertiesBySide } from "../../structure/RectProperties";
import type { PaddingStyleMap } from "../../user_options/InteriorOptions";
import { getVector3Index } from "./InteriorHelpers";

export type PadEdgeCutoutPoint = {
  point: Vector3;
  horizontal: "x" | "y" | "z";
};

export type InteriorPadBuildResult = PadDefinition & {
  edgeCutoutPoints: PadEdgeCutoutPoint[];
};

function snapToCaseBoundary(
  point: Vector3,
  horizontal: "x" | "y" | "z",
  caseInteriorSize: Vector3,
): Vector3 {
  const index = getVector3Index(horizontal);
  const boundary = caseInteriorSize[horizontal] / 2;
  const snapped = point.clone();
  const component = snapped.getComponent(index);
  snapped.setComponent(index, Math.sign(component || 1) * boundary);
  return snapped;
}

function collectWallPadEdgeCutoutPoints(
  padHelper: PadHelper,
  padSide: Sides,
  caseInteriorSize: Vector3,
): PadEdgeCutoutPoint[] {
  const rectProps = RectPropertiesBySide[padSide];
  if (!rectProps) {
    return [];
  }

  const { horizontal } = rectProps;
  const points: PadEdgeCutoutPoint[] = [];

  for (const padEdge of [PadSide.Left, PadSide.Right]) {
    const pos = padHelper.getEdgePosition(padEdge);
    if (!pos || !padHelper.sideTouchesCaseOuter(padEdge)) {
      continue;
    }
    points.push({
      point: snapToCaseBoundary(pos, horizontal, caseInteriorSize),
      horizontal,
    });
  }

  return points;
}

function collectBottomPadEdgeCutoutPoints(
  center: Vector3,
  size: [number, number],
  caseInteriorSize: Vector3,
): PadEdgeCutoutPoint[] {
  const points: PadEdgeCutoutPoint[] = [];
  const halfX = size[0] / 2;
  const halfZ = size[1] / 2;
  const boundaryTolerance = 0.01;

  const tryAxisPoint = (
    axis: "x" | "z",
    offset: number,
    horizontal: "x" | "z",
  ): void => {
    const index = getVector3Index(axis);
    const boundary = caseInteriorSize[axis] / 2;
    const component = center.getComponent(index) + offset;
    if (Math.abs(Math.abs(component) - boundary) > boundaryTolerance) {
      return;
    }
    const point = center.clone();
    point.setComponent(index, Math.sign(component || 1) * boundary);
    points.push({ point, horizontal });
  };

  for (const sign of [-1, 1] as const) {
    tryAxisPoint("x", sign * halfX, "x");
    tryAxisPoint("z", sign * halfZ, "z");
  }

  return points;
}

/**
 * Builds a synthetic `PaddingType` carrying the requested custom strength.
 *
 * Intentional trade-offs (matches the legacy `Pad` behavior in `oldcode/frontend.txt`):
 * - `name` is empty. Downstream price code (`getPriceForPart` -> case "Pad") uses
 *   `props.partSelection?.name` as a flag to decide between area-based pricing
 *   (m², known catalog foam) and volumetric pricing (m³, generic `STN_PLN_*`
 *   code per style). Empty name routes us to the volumetric branch, which is
 *   what the old code did when `OptionDB.get(...)` returned `undefined`.
 * - `strength` carries the exact requested value so renderers, BOM, and the
 *   volumetric price (`w * h * strength * 1e-9 m³`) all see the real thickness
 *   instead of a rounded catalog value.
 */
function createSyntheticPaddingType(
  style: PaddingStyle,
  padStrength: number,
): PaddingType {
  return {
    name: "",
    description: "Schaumstoff",
    kind: LTCaseUserOption.PaddingType,
    deleted: false,
    descriptionEn: "",
    availableExternally: false,
    strength: padStrength,
    style,
  };
}

export function getPaddingPartSelection(
  optionDb: OptionDB,
  style: PaddingStyle,
  padStrength: number,
): PaddingType {
  try {
    return optionDb.getOrThrow(LTCaseUserOption.PaddingType, {
      style,
      strength: padStrength,
    } as PaddingType);
  } catch {
    // No silent rounding to the closest catalog strength: that would quietly
    // change both the rendered thickness and the BOM/price code (e.g. SST1042
    // when the user configured 280 mm custom foam). Instead we synthesize a
    // PaddingType with the exact strength. The Pad still renders at the
    // requested thickness and pricing falls through to the volumetric branch
    // (`STN_PLN_29` / `STN_PEN_5023` / `STN_PEW_5023`, unit m³), mirroring the
    // old `Pad` fallback when `OptionDB.get(...)` returned `undefined`.
    return createSyntheticPaddingType(style, padStrength);
  }
}

export function buildBottomPad(
  center: Vector3,
  caseInteriorSize: Vector3,
  sizeWithPadding: Vector3,
  padding: { [side in Sides]: number },
  style: PaddingStyle,
  partSelection: PaddingType,
  buildstepName: string,
): InteriorPadBuildResult | null {
  const bottomPaddingHeight = padding[Sides.BOTTOM];
  // Skip pads with 0mm strength
  if (bottomPaddingHeight <= 0) {
    return null;
  }

  const padPosition = center.clone();
  padPosition.y = -caseInteriorSize.y / 2;
  const pad: PadDefinition = {
    id: "bottom_pad",
    position: padPosition,
    partSelection,
    side: Sides.BOTTOM,
    size: [
      sizeWithPadding.x - padding[Sides.LEFT] - padding[Sides.RIGHT],
      sizeWithPadding.z - padding[Sides.FRONT] - padding[Sides.BACK],
    ],
    strength: bottomPaddingHeight,
    style,
    type: LTPartType.Pad,
    rotation: new THREE.Euler(0, 0, 0),
    buildstepName,
    group: DefaultGroupName,
    orientation: 0,
    cutouts: [],
  };
  pad.position.z -= (padding[Sides.FRONT] - padding[Sides.BACK]) / 2;
  pad.position.x -= (padding[Sides.RIGHT] - padding[Sides.LEFT]) / 2;
  return {
    ...pad,
    edgeCutoutPoints: collectBottomPadEdgeCutoutPoints(
      pad.position,
      pad.size,
      caseInteriorSize,
    ),
  };
}

export function adjustPadHelperForHoodAndSides(
  padHelper: PadHelper,
  side: Sides,
  isHood: boolean,
): void {
  for (const padSide of [PadSide.Left, PadSide.Right]) {
    if (isHood) {
      if (side === Sides.LEFT || side === Sides.RIGHT) {
        if (
          !padHelper.isAtCaseOuter ||
          padHelper.sideTouchesCaseOuter(padSide)
        ) {
          padHelper.shrink(padSide);
        }
      } else if (
        !padHelper.isAtCaseOuter &&
        padHelper.sideTouchesCaseOuter(padSide)
      ) {
        padHelper.shrink(padSide);
      }
    } else if (side === Sides.LEFT || side === Sides.RIGHT) {
      padHelper.shrink(padSide);
    }
  }
}

/**
 * Sides whose padding a compartment does not build itself: the lid padding
 * (Deckelpolsterung) covers them. Stored values for these sides only take
 * effect once the optimizer may tip the compartment onto another side.
 */
export const PADDING_SIDES_BUILT_BY_COVER: ReadonlySet<Sides> = new Set([
  Sides.TOP,
]);

interface CreatePaddedCubeOptions {
  buildstepName: string;
  /** The option database the pads resolve their padding article in. */
  optionDb: OptionDB;
  innerPaddingHeight?: number;
  style?: PaddingStyle;
  styleBySide?: PaddingStyleMap;
}

export function createPaddedCube(
  caseInteriorSize: Vector3,
  padding: { [side in Sides]: number },
  center: Vector3,
  sizeWithPadding: Vector3,
  sidePaddingHeight: number,
  options: CreatePaddedCubeOptions,
): InteriorPadBuildResult[] {
  const {
    buildstepName,
    optionDb,
    innerPaddingHeight = sidePaddingHeight,
    style: defaultStyle = PaddingStyle.STANDARD,
    styleBySide,
  } = options;
  const result: InteriorPadBuildResult[] = [];
  const isHood = sidePaddingHeight !== innerPaddingHeight;

  for (const side in padding) {
    if (!Object.hasOwn(padding, side)) continue;
    const numericSide = Number(side) as Sides;
    const padStrength = padding[numericSide];
    if (padStrength <= 0) continue;

    const rectProps = RectPropertiesBySide[numericSide];
    if (!rectProps) continue;
    const { normal, normalDir, horizontal } = rectProps;
    const style = styleBySide?.[numericSide] ?? defaultStyle;

    // align center.y for current calculation
    center.y = (caseInteriorSize.y - innerPaddingHeight) / 2;

    const partSelection = getPaddingPartSelection(optionDb, style, padStrength);

    if (PADDING_SIDES_BUILT_BY_COVER.has(numericSide)) continue;

    if (numericSide === Sides.BOTTOM) {
      const bottomPad = buildBottomPad(
        center,
        caseInteriorSize,
        sizeWithPadding,
        padding,
        style,
        partSelection,
        buildstepName,
      );
      if (bottomPad) {
        result.push(bottomPad);
      }
      continue;
    }

    const padPosition = center
      .clone()
      .add(
        normalDir
          .clone()
          .multiplyScalar(
            sizeWithPadding.getComponent(getVector3Index(normal)) / 2,
          ),
      );
    padPosition.y = (-caseInteriorSize.y + sidePaddingHeight) / 2;

    const padHelper = new PadHelper(
      numericSide,
      sizeWithPadding.getComponent(getVector3Index(horizontal)),
      padPosition,
      caseInteriorSize,
      padding,
    );
    const edgeCutoutPoints = collectWallPadEdgeCutoutPoints(
      padHelper,
      numericSide,
      caseInteriorSize,
    );
    adjustPadHelperForHoodAndSides(padHelper, numericSide, isHood);

    result.push({
      ...padHelper.getProperties(
        padHelper.isAtCaseOuter ? sidePaddingHeight : innerPaddingHeight,
      ),
      id: "", // ID wird im BuildStep überschrieben (z.B. volume_pad_${counter})
      partSelection,
      style,
      side: numericSide,
      type: LTPartType.Pad,
      rotation: new THREE.Euler(0, 0, 0),
      buildstepName,
      group: DefaultGroupName,
      orientation: 0,
      cutouts: [],
      edgeCutoutPoints,
    });
  }
  return result;
}
